/**
 * Config del módulo de usuarios nuevo.
 *  - SEGMENT_ROLES: qué role.name cae en cada segmento.
 *  - roleSegment(name): bucketea un role.name → segmento.
 */

// OJO: son `role.name` REALES del backend, no alias del front. Dos estaban mal
// escritos y por eso esos usuarios caían en el bucket "otros" y NO se listaban
// en /admin/users (verificado contra la colección `roles`, 2026-07-26):
//   - "superadmin"  → el nombre real es "superadministrator"
//   - "supervisor"  → el nombre real es "supervisory" (name_public "Supervisor")
// Se dejan los alias históricos por si algún dato viejo los trae; el nombre
// canónico es el primero de cada familia.
export const SEGMENT_ROLES = {
  choferes: ["delivery", "delivery_freelance", "delivery_commerce_fleet"],
  clientes: ["key.account.manager", "client"],
  interna: [
    "administrator",
    "superadministrator",
    "supervisory",
    "support",
    "holding_admin",
    "supervisor",
    "superadmin",
    "holdingAdmin",
    "admin",
  ],
};

// Registro público co-brandeado del sitio de Beartrack. El `?from=<dominio>`
// resuelve la marca del operador y preasocia su logisticCompanyId en el wizard;
// el sitio normaliza el valor (tolera protocolo, www y path), así que se manda
// el campo `website` tal cual está guardado en la empresa. Compartido por
// Conductores (alta de choferes) y Empresas → Cuentas (alta de comercios).
const PUBLIC_REGISTER_BASE = "https://www.beartrackapp.com/registro";

export const buildRegisterUrl = (kind, website) => {
  const from = (website || "").trim();
  // Sin `website` configurado se abre el registro genérico: el visitante elige
  // operador de la lista. Mejor eso que un `?from=` vacío, que no matchea nada.
  return from
    ? `${PUBLIC_REGISTER_BASE}/${kind}?from=${encodeURIComponent(from)}`
    : `${PUBLIC_REGISTER_BASE}/${kind}`;
};

const ROLE_TO_SEGMENT = Object.entries(SEGMENT_ROLES).reduce((acc, [seg, roles]) => {
  roles.forEach((r) => { acc[r] = seg; });
  return acc;
}, {});

// Devuelve 'choferes' | 'clientes' | 'interna' | null (desconocido → null → bucket "otros")
export function roleSegment(roleName) {
  if (!roleName) return null;
  return ROLE_TO_SEGMENT[roleName] || null;
}

/**
 * ¿Se le puede poner la marca de VENDEDOR (`isSeller`) a este usuario?
 *
 * Se gatea por ROL y no por la pestaña donde aparece: la pestaña "clientes"
 * lista TODOS los usuarios de las empresas con contrato activo (users/clients
 * no filtra por rol), incluidos los repartidores de flota propia del comercio,
 * que tienen su PROPIA cuenta bancaria personal en el mismo campo `bankData`.
 * Si se gateara por pestaña, un repartidor podría quedar marcado como vendedor
 * y los consumidores del par (isSeller, bankData) —liquidación al comercio y
 * "a quién transferir" en la app del repartidor— leerían su cuenta personal
 * como si fuera la cuenta del comercio.
 */
export const canBeSeller = (user) =>
  roleSegment(user?.role?.name) === "clientes";

// ── Helpers de presentación compartidos por la lista (UsersModule) y el
//    detalle inline (UserDetail) ──

// Estado derivado del equipo interno: activo (aceptó) | pendiente (invitado,
// sin aceptar) | inactivo (desactivado). Mismo criterio que el equipo de comercio.
export function teamStatus(u) {
  if (u?.active) return "active";
  if (u?.newUser) return "pending";
  return "inactive";
}

// Tono del badge según rol. Los admin van en 'brand': el tone 'reverse' no
// existe en ui/Badge y caía silenciosamente a neutral.
export function roleTone(roleName = "") {
  const r = roleName.toLowerCase();
  if (r.includes("admin")) return "brand";
  if (r.startsWith("delivery")) return "info";
  if (r.includes("key.account") || r === "client") return "success";
  if (r.includes("supervisor")) return "warning";
  return "neutral";
}

export const initialsOf = (n, l) => `${(n || "").charAt(0)}${(l || "").charAt(0)}`.toUpperCase() || "?";

// role.name_public puede venir vacío en datos reales (visto para
// key.account.manager): se humaniza el name crudo como fallback
// ("key.account.manager" → "Key Account Manager"). Un name_public legítimo
// (sin puntos/guiones bajos) pasa intacto.
export const humanizeRole = (label = "") =>
  /[._]/.test(label)
    ? label.replace(/[._]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : label;
